import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const WorkspaceContext = createContext();

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [userPermissions, setUserPermissions] = useState({});

  // Initialize workspace when user logs in
  useEffect(() => {
    if (user) {
      initializeWorkspace();
    }
  }, [user]);

  const initializeWorkspace = () => {
    // Get or create default workspace
    const workspaceId = user?.id || 'default';
    const workspace = {
      id: workspaceId,
      name: `${user?.name || 'My'} Workspace`,
      ownerId: user?.id,
      createdAt: new Date().toISOString()
    };

    setCurrentWorkspace(workspace);

    // Load workspace data from localStorage
    const membersKey = `workspace_members_${workspaceId}`;
    const invitesKey = `workspace_invites_${workspaceId}`;
    
    const savedMembers = localStorage.getItem(membersKey);
    const savedInvites = localStorage.getItem(invitesKey);

    // Initialize with current user as owner if no members exist
    const members = savedMembers ? JSON.parse(savedMembers) : [
      {
        id: user?.id,
        name: user?.name,
        email: user?.email,
        avatar: user?.avatar,
        role: 'owner',
        joinedAt: new Date().toISOString()
      }
    ];

    const invites = savedInvites ? JSON.parse(savedInvites) : [];

    setWorkspaceMembers(members);
    setPendingInvites(invites);

    // Set current user permissions
    const currentUserMember = members.find(m => m.id === user?.id);
    setUserPermissions(getPermissions(currentUserMember?.role || 'owner'));
  };

  const getPermissions = (role) => {
    const permissions = {
      owner: {
        canInvite: true,
        canRemoveMembers: true,
        canChangeRoles: true,
        canManageQuestions: true,
        canCreateTemplates: true,
        canGeneratePapers: true,
        canViewAnalytics: true,
        canDeleteWorkspace: true
      },
      admin: {
        canInvite: true,
        canRemoveMembers: true,
        canChangeRoles: false,
        canManageQuestions: true,
        canCreateTemplates: true,
        canGeneratePapers: true,
        canViewAnalytics: true,
        canDeleteWorkspace: false
      },
      editor: {
        canInvite: false,
        canRemoveMembers: false,
        canChangeRoles: false,
        canManageQuestions: true,
        canCreateTemplates: true,
        canGeneratePapers: true,
        canViewAnalytics: false,
        canDeleteWorkspace: false
      },
      viewer: {
        canInvite: false,
        canRemoveMembers: false,
        canChangeRoles: false,
        canManageQuestions: false,
        canCreateTemplates: false,
        canGeneratePapers: false,
        canViewAnalytics: false,
        canDeleteWorkspace: false
      }
    };

    return permissions[role] || permissions.viewer;
  };

  const inviteTeamMember = async (inviteData) => {
    const invite = {
      id: Date.now().toString(),
      email: inviteData.email,
      role: inviteData.role,
      message: inviteData.message,
      invitedBy: user?.id,
      invitedByName: user?.name,
      workspaceId: currentWorkspace?.id,
      workspaceName: currentWorkspace?.name,
      sentAt: new Date().toISOString(),
      status: 'pending'
    };

    const updatedInvites = [...pendingInvites, invite];
    setPendingInvites(updatedInvites);

    // Save to localStorage
    const invitesKey = `workspace_invites_${currentWorkspace?.id}`;
    localStorage.setItem(invitesKey, JSON.stringify(updatedInvites));

    // Simulate sending email (in real app, this would be an API call)
    console.log('Invite sent:', invite);

    // Show success message
    alert(`Invitation sent to ${inviteData.email}`);

    return invite;
  };

  const acceptInvite = (inviteId, userData) => {
    const invite = pendingInvites.find(i => i.id === inviteId);
    if (!invite) return;

    // Add user to workspace members
    const newMember = {
      id: userData.id || Date.now().toString(),
      name: userData.name,
      email: invite.email,
      avatar: userData.avatar,
      role: invite.role,
      joinedAt: new Date().toISOString()
    };

    const updatedMembers = [...workspaceMembers, newMember];
    setWorkspaceMembers(updatedMembers);

    // Remove from pending invites
    const updatedInvites = pendingInvites.filter(i => i.id !== inviteId);
    setPendingInvites(updatedInvites);

    // Save to localStorage
    const membersKey = `workspace_members_${currentWorkspace?.id}`;
    const invitesKey = `workspace_invites_${currentWorkspace?.id}`;
    localStorage.setItem(membersKey, JSON.stringify(updatedMembers));
    localStorage.setItem(invitesKey, JSON.stringify(updatedInvites));
  };

  const updateMemberRole = (memberId, newRole) => {
    const updatedMembers = workspaceMembers.map(member =>
      member.id === memberId ? { ...member, role: newRole } : member
    );
    setWorkspaceMembers(updatedMembers);

    // Update permissions if it's the current user
    if (memberId === user?.id) {
      setUserPermissions(getPermissions(newRole));
    }

    // Save to localStorage
    const membersKey = `workspace_members_${currentWorkspace?.id}`;
    localStorage.setItem(membersKey, JSON.stringify(updatedMembers));
  };

  const removeMember = (memberId) => {
    const updatedMembers = workspaceMembers.filter(member => member.id !== memberId);
    setWorkspaceMembers(updatedMembers);

    // Save to localStorage
    const membersKey = `workspace_members_${currentWorkspace?.id}`;
    localStorage.setItem(membersKey, JSON.stringify(updatedMembers));
  };

  const cancelInvite = (inviteId) => {
    const updatedInvites = pendingInvites.filter(invite => invite.id !== inviteId);
    setPendingInvites(updatedInvites);

    // Save to localStorage
    const invitesKey = `workspace_invites_${currentWorkspace?.id}`;
    localStorage.setItem(invitesKey, JSON.stringify(updatedInvites));
  };

  const resendInvite = (inviteId) => {
    const updatedInvites = pendingInvites.map(invite =>
      invite.id === inviteId ? { ...invite, sentAt: new Date().toISOString() } : invite
    );
    setPendingInvites(updatedInvites);

    // Save to localStorage
    const invitesKey = `workspace_invites_${currentWorkspace?.id}`;
    localStorage.setItem(invitesKey, JSON.stringify(updatedInvites));

    alert('Invitation resent successfully');
  };

  const value = {
    currentWorkspace,
    workspaceMembers,
    pendingInvites,
    userPermissions,
    inviteTeamMember,
    acceptInvite,
    updateMemberRole,
    removeMember,
    cancelInvite,
    resendInvite,
    hasPermission: (permission) => userPermissions[permission] || false
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};